import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../App';
import { Button } from '../ui/button';
import { Pin, PinOff, Sparkles, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const STATUSES = [
  { value: 'under_review', label: 'Under review' },
  { value: 'planned', label: 'Planned' },
  { value: 'in_development', label: 'In development' },
  { value: 'released', label: 'Released' },
  { value: 'dismissed', label: 'Dismissed' },
];

const ROLE_FILTERS = [
  { value: '', label: 'All roles' },
  { value: 'provider', label: 'Providers' },
  { value: 'client', label: 'Clients' },
];

/**
 * Admin moderation surface for feature requests.
 *
 * Lists both pools with full author detail (name + email) so the team
 * can reach out, plus status + pin + dismiss controls. Filterable by
 * status and pool.
 */
export default function AdminFeatureRequestsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [expanded, setExpanded] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (roleFilter) params.set('role', roleFilter);
      const res = await api.get(`/admin/feature-requests${params.toString() ? `?${params.toString()}` : ''}`);
      setItems(res.data || []);
    } catch {
      setItems([]);
    } finally { setLoading(false); }
  }, [statusFilter, roleFilter]);

  useEffect(() => { load(); }, [load]);

  const update = async (item, patch) => {
    try {
      await api.put(`/admin/feature-requests/${item.request_id}`, patch);
      toast.success('Updated');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const toggle = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div className="card-surface p-6" data-testid="admin-feature-requests-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h3 className="font-heading text-lg font-medium text-[#1A1A1A] flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-[#D4AF37]" />
          Feature requests
          <span className="text-xs text-stone-400 font-normal">({items.length})</span>
        </h3>
        <div className="flex flex-wrap gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-xs px-2 py-1 border border-[#E6E4DD] rounded-lg bg-white"
            data-testid="admin-fr-status-filter"
          >
            <option value="">All statuses</option>
            {STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="text-xs px-2 py-1 border border-[#E6E4DD] rounded-lg bg-white"
            data-testid="admin-fr-role-filter"
          >
            {ROLE_FILTERS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-stone-400">Loading&hellip;</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-stone-400 text-center py-8">No requests match the current filter.</p>
      ) : (
        <ul className="space-y-2">
          {items.map(item => {
            const isOpen = expanded.has(item.request_id);
            return (
              <li
                key={item.request_id}
                className="border border-[#E6E4DD] rounded-lg bg-white"
                data-testid={`admin-fr-row-${item.request_id}`}
              >
                <button
                  type="button"
                  onClick={() => toggle(item.request_id)}
                  className="w-full p-3 flex items-start justify-between gap-3 text-left"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {item.pinned && <Pin className="h-3 w-3 text-[#D4AF37]" />}
                      <p className="text-sm font-medium text-[#1A1A1A]">{item.title}</p>
                      <span className="text-[10px] text-stone-500 bg-[#F9F6F0] border border-[#E6E4DD] px-1.5 py-0.5 rounded-full capitalize">
                        {item.author_role}
                      </span>
                      <span className="text-[10px] text-stone-500 bg-[#F9F6F0] border border-[#E6E4DD] px-1.5 py-0.5 rounded-full">
                        {item.status.replace('_', ' ')}
                      </span>
                      <span className="text-[10px] text-stone-500">&middot; {item.upvote_count} upvotes</span>
                    </div>
                    <p className="text-xs text-stone-500 mt-0.5">
                      {item.author_name || item.author_id} &middot; {item.author_email || 'no email'}
                    </p>
                  </div>
                  {isOpen ? <ChevronUp className="h-4 w-4 text-stone-400 mt-1" /> : <ChevronDown className="h-4 w-4 text-stone-400 mt-1" />}
                </button>
                {isOpen && (
                  <div className="px-3 pb-3 space-y-3 border-t border-[#E6E4DD] pt-3">
                    {item.description && (
                      <div>
                        <p className="text-[10px] text-stone-400 uppercase tracking-widest mb-1">Description</p>
                        <p className="text-xs text-stone-700 whitespace-pre-line">{item.description}</p>
                      </div>
                    )}
                    {item.why_useful && (
                      <div>
                        <p className="text-[10px] text-stone-400 uppercase tracking-widest mb-1">Why useful</p>
                        <p className="text-xs text-stone-700 whitespace-pre-line">{item.why_useful}</p>
                      </div>
                    )}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] text-stone-400 uppercase tracking-widest">Move to</span>
                      {STATUSES.map(s => (
                        <Button
                          key={s.value}
                          size="sm"
                          variant={item.status === s.value ? 'default' : 'outline'}
                          onClick={() => update(item, { status: s.value })}
                          className={`h-7 px-2 text-xs ${item.status === s.value ? 'btn-primary' : 'border-[#E6E4DD]'}`}
                          data-testid={`admin-fr-status-${item.request_id}-${s.value}`}
                          disabled={item.status === s.value}
                        >
                          {s.label}
                        </Button>
                      ))}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => update(item, { pinned: !item.pinned })}
                        className="h-7 px-2 text-xs border-[#E6E4DD] gap-1"
                        data-testid={`admin-fr-pin-${item.request_id}`}
                      >
                        {item.pinned ? <PinOff className="h-3 w-3" /> : <Pin className="h-3 w-3" />}
                        {item.pinned ? 'Unpin' : 'Pin to top'}
                      </Button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
